import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AppShell from './components/chrome/AppShell';
import Landing from './pages/public/Landing';
import Pricing from './pages/public/Pricing';
import FAQ from './pages/public/FAQ';
import Login from './pages/public/Login';
import Signup from './pages/public/Signup';
import Terms from './pages/public/Terms';
import Privacy from './pages/public/Privacy';
// Options is the critical-path page — keep it in the main chunk.
import Options from './pages/app/Options';
// Non-critical pages: lazy-loaded so their JS doesn't block the initial
// parse.  Browsers fetch these chunks on-demand when the user navigates
// to the route.  The spinner fallback covers the brief fetch latency
// (~100 ms on a warm CDN edge).
const Dashboard        = lazy(() => import('./pages/app/Dashboard'));
const GammaVisionIndex = lazy(() => import('./pages/app/GammaVisionIndex'));
const Risk             = lazy(() => import('./pages/app/Risk'));
const Simulations      = lazy(() => import('./pages/app/Simulations'));
const Scanner          = lazy(() => import('./pages/app/Scanner'));
const Settings         = lazy(() => import('./pages/app/Settings'));
import NotFound from './pages/NotFound';
import { useAuth } from './hooks/useAuth';
import { PUBLIC_ROUTES } from './lib/constants';
import { IdleProvider } from './contexts/IdleContext';
import RequireTier from './components/chrome/RequireTier';

/** Minimal loading fallback shown while a lazy chunk is fetching. */
const LazyFallback: React.FC = () => (
  <div className="gv-auth-redirect" role="status" aria-live="polite">
    <span className="gv-spinner" aria-hidden="true" />
  </div>
);

interface AuthGateProps {
  children: React.ReactNode;
}

const AuthGate: React.FC<AuthGateProps> = ({ children }) => {
  const { authed, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="gv-auth-redirect" role="status" aria-live="polite">
        <span className="gv-spinner" aria-hidden="true" />
        <span style={{ marginLeft: '0.6rem', color: 'var(--text-3)', fontSize: '0.85rem' }}>Verifying session…</span>
      </div>
    );
  }
  if (!authed) {
    // After login, redirect back to the attempted route — but only if
    // it isn't itself a public page (avoids redirect loops).
    const isPublic = (PUBLIC_ROUTES as readonly string[]).includes(location.pathname);
    return (
      <Navigate
        to="/login"
        replace
        state={!isPublic ? { from: location.pathname + location.search + location.hash } : undefined}
      />
    );
  }
  return <>{children}</>;
};

const App: React.FC = () => (
  <BrowserRouter>
    {/* IdleProvider tracks document-level activity and exposes an
        `idle` boolean. useWebSocket reads it and closes its sockets
        after 15 min of inactivity (or instantly on tab-hidden), so a
        backgrounded tab stops pulling data + costing VM bandwidth.
        Public pages don't use WS, so the context is a no-op for them.
        Wrapped here at the App root so the same provider survives
        route transitions and the timer doesn't reset on navigation. */}
    <IdleProvider>
      <Routes>
        {/* Public routes */}
        <Route path="/"        element={<AppShell><Landing /></AppShell>} />
        <Route path="/login"   element={<AppShell showFooter={false}><Login /></AppShell>} />
        <Route path="/signup"  element={<AppShell showFooter={false}><Signup /></AppShell>} />
        <Route path="/pricing" element={<AppShell><Pricing /></AppShell>} />
        <Route path="/faq"     element={<AppShell><FAQ /></AppShell>} />
        <Route path="/terms"   element={<AppShell><Terms /></AppShell>} />
        <Route path="/privacy" element={<AppShell><Privacy /></AppShell>} />

        {/*
          Authed + tier-gated routes.

          AuthGate: must be logged in. Bounces to /login if not.
          RequireTier: must be at least the specified tier — admins
                       always pass. Renders <UpgradeNudge> in-place
                       if the user is below.

          Tier matrix (locked in Day 3):
            free        — /app/settings only (stripped to Account)
            essentials  — + /app/options (SPY/SPX/QQQ only)
            pro         — + Dashboard, Risk, Simulations, Scanner,
                          Index, full Options (any ticker + replay)

          Per-ticker filtering for Essentials (e.g. blocking NVDA on
          /app/options) happens inside the page using user_can_access_ticker
          + the search box hides results outside the tier's allow-list.
        */}
        <Route path="/app" element={<Navigate to="/app/options" replace />} />
        {/* Options stays eagerly loaded — primary destination after
            login, must render without chunk fetch latency. Tier-gated
            to Essentials (Free users hit the UpgradeNudge). */}
        <Route path="/app/options"     element={<AuthGate><RequireTier minimum="essentials" featureLabel="Options Heatmaps"><AppShell showFooter={false}><Options /></AppShell></RequireTier></AuthGate>} />
        {/* Pro-tier-gated pages, lazy-loaded for bundle savings.
            Each <Suspense> is scoped inside the AppShell so the
            chrome stays visible during chunk fetch. */}
        <Route path="/app/dashboard"   element={<AuthGate><RequireTier minimum="pro" featureLabel="Dashboard"><AppShell><Suspense fallback={<LazyFallback />}><Dashboard /></Suspense></AppShell></RequireTier></AuthGate>} />
        <Route path="/app/index"       element={<AuthGate><RequireTier minimum="pro" featureLabel="GammaVision Index"><AppShell><Suspense fallback={<LazyFallback />}><GammaVisionIndex /></Suspense></AppShell></RequireTier></AuthGate>} />
        <Route path="/app/risk"        element={<AuthGate><RequireTier minimum="pro" featureLabel="Risk Monitor"><AppShell><Suspense fallback={<LazyFallback />}><Risk /></Suspense></AppShell></RequireTier></AuthGate>} />
        <Route path="/app/simulations" element={<AuthGate><RequireTier minimum="pro" featureLabel="Simulations"><AppShell><Suspense fallback={<LazyFallback />}><Simulations /></Suspense></AppShell></RequireTier></AuthGate>} />
        <Route path="/app/scanner"     element={<AuthGate><RequireTier minimum="pro" featureLabel="Index Scanner"><AppShell><Suspense fallback={<LazyFallback />}><Scanner /></Suspense></AppShell></RequireTier></AuthGate>} />
        {/* Settings: no tier gate (every authed tier can reach it).
            Lazy-loaded; Settings renders different content per tier
            (stripped Account-only view for Free). */}
        <Route path="/app/settings"    element={<AuthGate><AppShell><Suspense fallback={<LazyFallback />}><Settings /></Suspense></AppShell></AuthGate>} />

        {/* 404 */}
        <Route path="*" element={<AppShell><NotFound /></AppShell>} />
      </Routes>
    </IdleProvider>
  </BrowserRouter>
);

export default App;
